import { ArrowUpRight } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';

import { ProductLogo, type CategoryDto, type ProductListing } from '@entities/product';
import { ButtonLink } from '@shared/ui/Button';

import styles from './HomepageHighlights.module.css';

export interface HomepageHighlightsProps {
  listings: ProductListing[];
  categories: CategoryDto[];
}

const productLink = (listing: ProductListing) => `/product/${encodeURIComponent(listing.primary.slug)}`;
const categoryLink = (category: CategoryDto) => `/?category=${encodeURIComponent(category.id)}#catalog`;

/** Homepage discovery banners use the same live catalogue as the product rows. */
export function HomepageHighlights({ listings, categories }: HomepageHighlightsProps) {
  const picks = [...listings].sort((left, right) =>
    Number(right.primary.inStock) - Number(left.primary.inStock)
    || Number(right.primary.popular) - Number(left.primary.popular),
  ).slice(0, 3);
  const featured = picks[0];
  if (!featured) return null;

  const collections = categories.flatMap(category => {
    const product = picks.find(listing => listing.primary.category?.id === category.id)
      ?? listings.find(listing => listing.primary.category?.id === category.id);
    return product ? [{ category, product }] : [];
  }).slice(0, 3);

  return (
    <section className={styles.highlights} aria-label="Discover digital products">
      <div className={styles.heroGrid} data-single={picks.length === 1 || undefined}>
        <div className={styles.feature}>
          <div className={styles.featureCopy}>
            <h2>Digital products, all in one place</h2>
            <p>Browse software, templates and learning resources.</p>
            <ButtonLink className={styles.featureAction} to={productLink(featured)} aria-label={`Explore ${featured.name}`}>Explore product <ArrowUpRight size={18} weight="bold" aria-hidden /></ButtonLink>
          </div>
          <div className={styles.featureArt} aria-hidden>
            <span className={styles.orbit} />
            <div className={styles.mainMark}>
              <ProductLogo src={featured.primary.logoUrl} className={styles.mainLogo} loading="eager" decoding="async" iconSize={64} />
            </div>
            {picks[1] && <div className={styles.floatingMark}><ProductLogo src={picks[1].primary.logoUrl} className={styles.floatingLogo} iconSize={28} /></div>}
            {picks[2] && <div className={`${styles.floatingMark} ${styles.floatingMarkSecond}`}><ProductLogo src={picks[2].primary.logoUrl} className={styles.floatingLogo} iconSize={28} /></div>}
          </div>
        </div>

        {picks.length > 1 && <div className={styles.sideBanners}>
          {picks.slice(1).map((listing, index) => (
            <Link className={`${styles.sideCard} ${index === 0 ? styles.lilacCard : styles.inkCard}`} to={productLink(listing)} key={listing.key} aria-label={`Explore ${listing.name}`}>
              <div className={styles.sideCopy}>
                <h3>{listing.name}</h3>
                <span className={styles.sideAction}>View product <ArrowUpRight size={16} weight="bold" aria-hidden /></span>
              </div>
              <span className={styles.sideArt} aria-hidden><ProductLogo src={listing.primary.logoUrl} className={styles.sideLogo} iconSize={36} /></span>
            </Link>
          ))}
        </div>}
      </div>

      {collections.length > 0 && <div className={styles.collections}>
        {collections.map(({ category, product }, index) => (
          <Link className={styles.collection} to={categoryLink(category)} key={category.id} data-tone={index} aria-label={`Explore ${category.name}`}>
            <div className={styles.collectionCopy}>
              <h3>{category.name}</h3>
              <span className={styles.collectionAction}>Browse category <ArrowUpRight size={15} aria-hidden /></span>
            </div>
            <span className={styles.collectionArt} aria-hidden><ProductLogo src={product.primary.logoUrl} className={styles.collectionLogo} iconSize={28} /></span>
          </Link>
        ))}
      </div>}
    </section>
  );
}
